import Player from '../../Player';
import * as ConvivaLib from '../../lib/Conviva_Videojs_2.121.0.32773.js';

/**
 * Conviva functionality for video player.
 * @class
 */
class Conviva {
  /**
   * Instantiate the plugin.
   * @param {Player} player - Must be a Player instance.
   * @param {object} config - Conviva configuration.
   * @returns {Conviva}
   */
  constructor(player, config) {
    if (!(player instanceof Player)) {
      throw 'Must initialize with instance of Player.';
    }

    this.config = config;
    this.timeout = this.config.timeout || '1000';
    this.player = player;
    this.isReady = false;
    this.playerSessions = [];

    if (typeof ConvivaLib !== 'undefined') {
      this.initialize();
      return this;
    }

    return this;
  }

  /**
   * Getter for conviva library.  Primarily here so that
   *  Conviva can be stubbed in tests.
   */
  getConviva() {
    return ConvivaLib;
  }

  /**
   * On plugin load, this is where we initialize it.
   * @private
   * @listens Player#ready
   */
  initialize() {
    this.isReady = true;

    if (this.config.touchstoneUrl) {
      this.getConviva().LivePass.initWithSettings(this.config.key, {
        gatewayUrl: this.config.touchstoneUrl
      });
    } else {
      this.getConviva().LivePass.init(this.config.key);
    }

    // We have to bind error here, since they can occur
    //  before player 'ready';
    // this.player.on('error', e => { this.reportError(e) });

    this.bindEvents(this.player);
    this.bindAdEvents();

    // Bind player events when ready.
    // this.player.on('ready', () => {
    // })

    // If debug is enabled, toggle traces.
    if (this.config.debug) {
      this.getConviva().LivePass.toggleTraces(true);
    }
  }

  setMetaData(convivaMetadata) {
    if (!this.config.metaData) {
      return convivaMetadata;
    }
    convivaMetadata = this.assignConvivaObject(
      convivaMetadata,
      this.config.metaData
    );
    return convivaMetadata;
  }

  assignConvivaObject(source, target) {
    for (let metaKey in target) {
      if (target.hasOwnProperty(metaKey)) {
        source[metaKey] = target[metaKey];
      }
    }
    return source;
  }

  getSessionId() {
    const currentItem = this.getCurrentItem();
    return currentItem.convivaSessionId >= 0
      ? currentItem.convivaSessionId
      : undefined;
  }

  setSessionId(id) {
    const currentItem = this.getCurrentItem();
    currentItem.convivaSessionId = id;
    this.playerSessions.push(id);
  }

  clearSessionId() {
    const currentItem = this.getCurrentItem();
    currentItem.convivaHasPlayed = true;
    delete currentItem.convivaSessionId;
  }

  isSessionAttached() {
    const currentItem = this.getCurrentItem();
    return currentItem.convivaSessionAttached || false;
  }

  detachSession() {
    const currentItem = this.getCurrentItem();
    delete currentItem.convivaSessionAttached;
  }

  getCurrentItem() {
    return this.player.getCurrentPlaylistItem();
  }

  cleanupPlayerSessions() {
    this.playerSessions.map(id => {
      this.cleanupConvivaSession(id);
      this.player.log('[CONVIVA]', '[EXTRA SESSION CLEANED]', id);
    });
    this.playerSessions = [];
  }

  cleanupConvivaSession(sessionId) {
    this.getConviva().LivePass.detachStreamer(sessionId);
    this.getConviva().LivePass.cleanupSession(sessionId);
  }

  getAssetName() {
    const video = this.player.getCurrentPlaylistItem();
    return video.convivaAssetName || video.title || 'No Title';
  }

  /**
   * Start a new Conviva session.
   */
  startSession() {
    if (typeof this.getSessionId() !== 'undefined') {
      return;
    }

    // Cleanup other player sessions.
    this.cleanupPlayerSessions();

    const video = this.player.getCurrentPlaylistItem();
    const gaId = this.getCookie('_ga');
    const assetName = this.getAssetName();
    const ipid = this.getCookie('ipid');
    const tags = Object.assign(
      {},
      {
        VideoID: (video.contentId || '0').toString(),
        Site: this.config.site || window.location.host,
        PlayerVendor: 'WWEVideoPlayer',
        PlayerVersion: Player.getVersion(),
        IPID: ipid
      },
      this.config.tags || {}
    );
    const _Conviva = this.getConviva();
    const streamUrl = this.player.src();
    let convivaMetadata = new _Conviva.ConvivaContentInfo(assetName, tags);
    convivaMetadata.defaultReportingCdnName = 'AKAMAI'; // required
    convivaMetadata.streamUrl = streamUrl; // required
    convivaMetadata.isLive = this.config.isLive || false; // required
    (convivaMetadata.viewerId = (gaId ? gaId.split('.')[2] : 0).toString()),
      (convivaMetadata.playerName = this.config.playerName || 'videojs');
    convivaMetadata.frameworkVersion = Player.getInternalVersion();

    // Override meta data with any provided in the config.
    convivaMetadata = this.setMetaData(convivaMetadata);

    // Enabling external bitrate reporting per WWEC-5155
    const options = {};
    // options[this.getConviva().LivePass.OPTION_EXTERNAL_BITRATE_REPORTING] = true;

    const sessionId = this.getConviva().LivePass.createSession(
      null,
      convivaMetadata,
      options
    );

    this.setSessionId(sessionId);

    this.player.log('[CONVIVA]', '[SESSION STARTED]', this.getSessionId());
  }

  /**
   * Bind all necessary to their respective callbacks.
   * @param {Player} player
   * @private
   */
  bindEvents(player) {
    player.on('play', e => this.onPlay(e));
    // player.on('qualitychanged', e => { this.qualityChanged(e) });
    player.on('error', () => {
      this.reportError();
    });
    player.on('ended', e => {
      this.cleanUpSession(e);
    });
    player.on('playbackstalled', () => {
      this.onPlaybackStalled();
    });
  }

  /**
   * Report an error if playback has stalled.
   */
  onPlaybackStalled() {
    const _Conviva = this.getConviva();

    this.reportError({
      message: 'Playback stalled',
      severity: _Conviva.StreamerError.SEVERITY_FATAL
    });
    this.cleanUpSession();
  }

  onPlay() {
    // If no session, create one.
    if (typeof this.getSessionId() === 'undefined') {
      this.startSession();
    }

    // if an ad is playing, bind attach session
    //  to the ad ended event.
    if (this.delayAttachStream()) {
      // this.player.once('adEnded', () => this.attachSession())
      return;
    }

    // Otherwise, attach the session now.
    this.attachSession();
  }

  delayAttachStream() {
    // Are we playing an ad right now?
    if (this.player.isLinearAdPlaying()) {
      return true;
    }
    const currentItem = this.getCurrentItem();

    if (currentItem._convivaAdPlayed) {
      return false;
    }

    // Are we going to play an ad?
    return (
      this.player.getCurrentTime() <= 0 &&
      this.player.adType() === 'dai' &&
      currentItem.contentId &&
      currentItem.steamHasStitchedAd
    );
  }

  updateMetaData(newMetaData) {
    const _Conviva = this.getConviva();
    const assetName = this.getAssetName();
    let metaData = new _Conviva.ConvivaContentInfo(assetName);
    metaData = this.assignConvivaObject(metaData, newMetaData);
    _Conviva.LivePass.updateContentMetadata(this.getSessionId(), metaData);
    this.player.log('[CONVIVA]', 'Updated Meta Data', metaData);
  }

  /**
   * Bind ad events
   * @private
   */
  bindAdEvents() {
    this.player.on('adLoaded', e => this.adLoaded(e));
    this.player.on('adStart', () => this.adStarted());
    this.player.on('adEnded', () => this.adEnded());
    this.player.on('adError', e => this.adError(e));
  }

  adLoaded(event) {
    if (typeof event.getStreamData === 'function') {
      const data = event.getStreamData();
      this.newStreamUrl = data.url;
    }
  }

  /**
   * Fires adStart event on conviva.
   *
   * @event Conviva#adStarted
   * @private
   */
  adStarted() {
    // Start a session if it doesnt exist since this event
    //  can happen very early.
    if (!this.getSessionId()) {
      this.startSession();
    }

    this.player.log('[CONVIVA]', '[AD STARTED]', this.getSessionId());
    this.getConviva().LivePass.adStart(this.getSessionId());
  }

  /**
   * Fires adEnded event on conviva.
   *
   * @event Conviva#adEnded
   * @private
   */
  adEnded() {
    this.player.log('[CONVIVA]', '[AD ENDED]', this.getSessionId());

    // Call ad ended.
    this.getConviva().LivePass.adEnd(this.getSessionId());

    // Trigger session attach.
    setTimeout(() => this.attachSession(), true);

    // Set current item ad played.
    this.setAdPlayed();
  }

  /**
   * Sets a property on current ad to "played"
   */
  setAdPlayed() {
    const currentItem = this.getCurrentItem();
    currentItem._convivaAdPlayed = true;
  }

  /**
   * Ad error callback.
   * @param event
   */
  adError(event) {
    const currentItem = this.player.getCurrentPlaylistItem();

    // Fire the error.
    this.fireCustomError('adError', {
      errorMessage: (event.errorMessage || 'Unknown error').substring(0, 25),
      vmsId: '' + currentItem.contentId
    });

    // Need to try to attach session here because normally
    //  we rely on the ad ended event to attach.
    setTimeout(() => this.attachSession(), true);

    // Set current item ad played.
    this.setAdPlayed();
  }

  /**
   * On First frame callback
   */
  onFirstFrame() {
    if (this.player.isLinearAdPlaying()) {
      this.player.once('adEnded', () => this.attachSession());
    } else {
      this.attachSession();
    }
  }

  attachSession() {
    if (typeof this.getSessionId() === 'undefined') {
      this.startSession();
    }

    if (this.isSessionAttached()) {
      return;
    }

    const _Conviva = this.getConviva();

    // Fix for mock race condition.
    if (!_Conviva.ConvivaVideojsStreamerProxy) {
      return;
    }

    const streamer = new _Conviva.ConvivaVideojsStreamerProxy(
      this.player.currentPlayer
    );
    streamer.SetPlayingState('PLAYING');
    _Conviva.LivePass.attachStreamer(this.getSessionId(), streamer);

    // Attach to current item.
    const currentItem = this.getCurrentItem();
    currentItem.convivaSessionAttached = true;

    // If new stream URL.
    if (this.newStreamUrl) {
      this.updateMetaData({ streamUrl: this.newStreamUrl });
      delete this.newStreamUrl;
    }

    this.player.log('[CONVIVA]', '[SESSION ATTACHED]', this.getSessionId());
  }

  /**
   * Fired on HLS quality change.
   *
   * @event Conviva#qualityChanged
   * @param event
   */
  qualityChanged(event) {
    if (!event.bitrate) {
      return;
    }
    this.getConviva().LivePass.setBitrate(
      this.getSessionId(),
      this.getKbpsFromBps(event.bitrate)
    );
  }

  /**
   * Report an error to Conviva lib.
   *
   * @event Conviva#reportError
   * @param error
   */
  reportError(error) {
    // If no session ID, we may have a VSF or VPF, so
    //  we need a session instance.
    const _error = error || this.player.getError();
    if (this.getSessionId() === undefined) {
      this.startSession();
    }
    this.getConviva().LivePass.reportError(
      this.getSessionId(),
      _error.message || 'General error',
      _error.severity || undefined
    );
  }

  /**
   * Returns a Kbps value from a Bps value
   *
   * @param {integer} bps
   * @returns {number}
   */
  getKbpsFromBps(bps) {
    if (!bps) return 0;
    return Math.round(bps / 1024);
  }

  fireCustomError(eventType, event) {
    if (this.getSessionId() === 'undefined' || !eventType) {
      return;
    }
    this.getConviva().LivePass.sendEvent(eventType, event || {});
  }

  /**
   * Helper function to get a browser cookie.
   *
   * @param {string} cName
   * @returns {string}
   */
  /* istanbul ignore next */
  getCookie(cName) {
    cName = cName + '=';
    const allCookies = document.cookie;
    const cPos = allCookies.indexOf(cName);
    if (cPos === -1) {
      return '';
    }
    const cStart = cPos + cName.length;
    let cEnd = allCookies.indexOf(';', cStart);
    if (cEnd === -1) {
      cEnd = allCookies.length;
    }
    return decodeURI(allCookies.substring(cStart, cEnd));
  }

  /**
   * Cleans up Conviva session.
   */
  cleanUpSession(sessionId) {
    const _sessionId = sessionId || this.getSessionId();
    if (typeof _sessionId !== 'undefined') {
      this.cleanupConvivaSession(_sessionId);
      this.detachSession();
      this.clearSessionId();
      this.player.log('[CONVIVA]', '[SESSION CLEANED]', _sessionId);
    }
  }
}

export default Conviva;
